/**
 * ADR-122 — Emails: the firm's staff change the wording of the emails their clients receive,
 * themselves. No code release, no deploy: a save is used by the next email sent.
 *
 * <h3>Two screens</h3>
 * <p>`/emails` lists the emails the signed-in person may reword (the engine's answer — only emails
 * whose declared role they hold), each with whether the firm's own wording or the default is in use,
 * and who changed it last. `/emails/<id>` is the editor: the subject and the text, the details the
 * platform fills in shown as grey `[Client name]`-style boxes, a live preview of what the client will
 * see, Save, and Reset to default wording.</p>
 *
 * <h3>What the page never decides</h3>
 * <p>Who may edit, which details an email may use, that the link must stay, and whether a wording can
 * be sent are all checked by the engine on every save (L-64); the page says the engine's answer in
 * words. It shows the link rule early too (a missing link is flagged as you type) only so nobody
 * finds out at Save.</p>
 *
 * <h3>The preview is a sandboxed frame</h3>
 * <p>The HTML comes from the engine, built from the text with every typed character encoded. It is
 * still drawn in an `<iframe sandbox>` with no permissions — no script, no navigation out — because a
 * preview of an email is a document, not part of this page.</p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, PageHeader, Pill, Table, type Column } from "@ardept/design-system";
import {
  describeEmailError,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  useClientTenant,
  useEmailTemplates,
  type EmailFailure,
} from "../api/portalQueries.js";
import type { EmailSaveResponse, EmailTemplate, EmailTemplatesResponse } from "../api/types.js";
import { EMAILS_LABEL, EMAILS_ROUTE } from "../nav/familyRegistry.js";
import { useTenantQuery } from "../nav/TenantSelection.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import { editedWhen, fromEditor, missingRequired, pieces, toEditor } from "./emailWording.js";
import "./FileDetailPage.css";
import "./ImportPage.css";
import "./EmailsPage.css";
import { useVendorCopy } from "../theme/vendorCopy.js";

/** How long typing must pause before the preview is asked for again. */
export const PREVIEW_DEBOUNCE_MS = 400;

// ── The list ────────────────────────────────────────────────────────────────────────────────

interface Row {
  id: string;
  template: EmailTemplate;
}

/** The status of an email's wording, as a pill. */
function WordingPill({ template }: { template: EmailTemplate }): JSX.Element {
  if (!template.is_edited) return <Pill tone="neutral">Default wording</Pill>;
  if (template.default_changed_since_edit === true) return <Pill tone="attention">Default has changed</Pill>;
  return <Pill tone="good">Your wording</Pill>;
}

/** "Changed by a@b on 6 Oct 2026 at 14:05", or nothing for an email using the default. */
function lastChanged(template: EmailTemplate): string | null {
  if (!template.is_edited) return null;
  const when = editedWhen(template.edited_at);
  const who = template.edited_by;
  if (who && when) return `Changed by ${who} on ${when}`;
  if (when) return `Changed on ${when}`;
  return "Changed by your firm";
}

export function EmailsPage(): JSX.Element {
  const { contact } = useVendorCopy();
  usePageTitle(EMAILS_LABEL);
  const identity = useClientTenant();
  const list = useEmailTemplates(identity.tenantId);
  const tenantQuery = useTenantQuery();

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        key: "email",
        label: "Email",
        render: (row) => (
          <div className="em-row">
            <Link className="em-row__title" to={{ pathname: `${EMAILS_ROUTE}/${encodeURIComponent(row.id)}`, search: tenantQuery }}>
              {row.template.label}
            </Link>
            {row.template.description && <span className="em-row__desc">{row.template.description}</span>}
          </div>
        ),
      },
      { key: "wording", label: "Wording", render: (row) => <WordingPill template={row.template} /> },
      {
        key: "changed",
        label: "Last changed",
        render: (row) => <span className="em-row__desc">{lastChanged(row.template) ?? "Not changed"}</span>,
      },
    ],
    [tenantQuery],
  );

  return (
    <div className="emails-page" data-testid="emails-page">
      <PageHeader
        title={EMAILS_LABEL}
        subtitle="Change the wording of the emails your clients receive. A change is used from the next email sent."
      />
      {list.error !== null && (
        <div className="state state--error" role="alert">
          <p className="state__body">We could not load your emails just now. Refresh the page to try again.</p>
        </div>
      )}
      {list.data === undefined && list.error === null && (
        <EmptyState iconName="file-edit" title="Loading…" body="Finding the emails you can change." />
      )}
      {list.data !== undefined && list.data.templates.length === 0 && (
        <div className="state" role="status">
          <h2 className="state__title">There are no emails for you to change</h2>
          <p className="state__body">
            Changing client emails is limited to the people your firm has chosen. Speak to {contact} if you
            think you should be one of them.
          </p>
        </div>
      )}
      {list.data !== undefined && list.data.templates.length > 0 && (
        <section className="im-card em-card" aria-label="Emails you can change">
          <Table<Row>
            columns={columns}
            rows={list.data.templates.map((template) => ({ id: template.template_id, template }))}
            caption="Emails you can change"
          />
        </section>
      )}
    </div>
  );
}

// ── The editor ──────────────────────────────────────────────────────────────────────────────

export function EmailEditorPage(): JSX.Element {
  const { templateId = "" } = useParams<{ templateId: string }>();
  const identity = useClientTenant();
  const list = useEmailTemplates(identity.tenantId);
  const template = list.data?.templates.find((t) => t.template_id === templateId);
  usePageTitle(template?.label ?? EMAILS_LABEL);
  const { search } = useLocation();
  // Survives the editor re-mounting on a new version, so "Saved" is still said after the save.
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <div className="emails-page" data-testid="email-editor">
      <Link className="fd-back" to={{ pathname: EMAILS_ROUTE, search }}>
        <span aria-hidden="true">&larr;</span> All emails
      </Link>
      {list.error !== null && (
        <div className="state state--error" role="alert">
          <p className="state__body">We could not load this email just now. Refresh the page to try again.</p>
        </div>
      )}
      {list.data === undefined && list.error === null && (
        <EmptyState iconName="file-edit" title="Loading…" body="Opening the email." />
      )}
      {list.data !== undefined && template === undefined && (
        <div className="state" role="status">
          <h1 className="state__title">This email can&rsquo;t be changed here</h1>
          <p className="state__body">It is not one of the emails you can change. Go back to the list to choose another.</p>
        </div>
      )}
      {template !== undefined && identity.tenantId !== null && (
        <EmailEditor
          key={`${template.template_id}:${template.version ?? "default"}:${template.default_subject}`}
          tenantId={identity.tenantId}
          template={template}
          notice={notice}
          onNotice={setNotice}
        />
      )}
    </div>
  );
}

function EmailEditor({
  tenantId,
  template,
  notice,
  onNotice,
}: {
  tenantId: string;
  template: EmailTemplate;
  notice: string | null;
  onNotice: (notice: string | null) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const fields = template.fields;
  const initialSubject = useMemo(() => toEditor(template.subject, fields), [template, fields]);
  const initialBody = useMemo(() => toEditor(template.body_text, fields), [template, fields]);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [confirmReset, setConfirmReset] = useState(false);
  const [failure, setFailure] = useState<EmailFailure | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  const lastFocus = useRef<"subject" | "body">("body");
  const highlightRef = useRef<HTMLDivElement | null>(null);

  const dirty = subject !== initialSubject || body !== initialBody;
  const missing = missingRequired(body, fields);

  // The preview: the wording in use while nothing is changed; the edit (debounced) once it is.
  const [debounced, setDebounced] = useState({ subject, body });
  useEffect(() => {
    const timer = setTimeout(() => setDebounced({ subject, body }), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [subject, body]);
  const previewDirty = debounced.subject !== initialSubject || debounced.body !== initialBody;
  const canonical = previewDirty
    ? { subject: fromEditor(debounced.subject, fields), body_text: fromEditor(debounced.body, fields) }
    : null;
  const preview = useQuery({
    queryKey: ["email-preview", tenantId, template.template_id, template.version, canonical?.subject ?? null, canonical?.body_text ?? null],
    queryFn: () => previewEmailTemplate(tenantId, template.template_id, canonical ?? {}),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previous) => previous,
  });
  const previewProblems = !preview.isPlaceholderData && previewDirty ? (preview.data?.problems ?? []) : [];

  const updateList = (saved: EmailSaveResponse) => {
    queryClient.setQueryData<EmailTemplatesResponse>(["email-templates", tenantId], (old) =>
      old === undefined
        ? old
        : { templates: old.templates.map((t) => (t.template_id === saved.template.template_id ? saved.template : t)) },
    );
  };

  const save = useMutation<EmailSaveResponse, Error, void>({
    mutationFn: () =>
      saveEmailTemplate(tenantId, template.template_id, {
        subject: fromEditor(subject, fields),
        body_text: fromEditor(body, fields),
        based_on_version: template.version,
      }),
    onMutate: () => {
      setFailure(null);
      onNotice(null);
    },
    onSuccess: (saved) => {
      onNotice("Saved. The next email sent will use this wording.");
      updateList(saved);
    },
    onError: (error) => setFailure(describeEmailError(error)),
  });

  const reset = useMutation<EmailSaveResponse, Error, void>({
    mutationFn: () => resetEmailTemplate(tenantId, template.template_id),
    onMutate: () => {
      setFailure(null);
      onNotice(null);
    },
    onSuccess: (saved) => {
      setConfirmReset(false);
      onNotice("Reset. The next email sent will use the default wording.");
      updateList(saved);
    },
    onError: (error) => {
      setConfirmReset(false);
      setFailure(describeEmailError(error));
    },
  });

  /** Put a detail's box where the cursor was (in the subject or the text). */
  const insert = (label: string) => {
    const token = `[${label}]`;
    const inSubject = lastFocus.current === "subject";
    const el = inSubject ? subjectRef.current : bodyRef.current;
    const value = inSubject ? subject : body;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    if (inSubject) setSubject(next);
    else setBody(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const discard = () => {
    setSubject(initialSubject);
    setBody(initialBody);
    setFailure(null);
  };

  const busy = save.isPending || reset.isPending;
  const canSave = dirty && !busy && subject.trim() !== "" && body.trim() !== "" && missing.length === 0 && previewProblems.length === 0;
  const changed = lastChanged(template);
  const problems = failure?.problems.length ? failure.problems : previewProblems;

  return (
    <>
      <PageHeader title={template.label} subtitle={template.description ?? undefined} actions={<WordingPill template={template} />} />
      {changed && <p className="em-meta">{changed}</p>}

      {template.default_changed_since_edit === true && (
        <div className="em-banner" role="status">
          <p className="em-banner__title">The default wording for this email has changed since your wording was saved.</p>
          <p className="em-banner__body">
            Your wording is still the one being sent. Read the new default below, then keep your wording or reset to
            the default.
          </p>
        </div>
      )}

      {notice !== null && (
        <div className="em-notice" role="status">
          {notice}
        </div>
      )}

      <div className="em-layout">
        <section className="im-card" aria-labelledby="em-edit-h">
          <div className="im-card__head">
            <h2 id="em-edit-h" className="im-card__title">
              Wording
            </h2>
          </div>
          <div className="im-card__body">
            <label className="lt-field">
              <span className="lt-label">Subject</span>
              <input
                ref={subjectRef}
                className="ds-input"
                value={subject}
                maxLength={300}
                onFocus={() => (lastFocus.current = "subject")}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>

            <div className="lt-field">
              <label className="lt-label" htmlFor="em-body">
                Email
              </label>
              <span className="lt-hint" id="em-body-hint">
                Leave a blank line to start a new paragraph. The grey boxes are filled in for each client when the email
                is sent.
              </span>
              <div className="em-editor">
                <div className="em-editor__highlight" ref={highlightRef} aria-hidden="true">
                  {pieces(body, fields).map((piece, i) =>
                    piece.kind === "field" ? (
                      <mark key={i} className={`em-chip${piece.field.required ? " em-chip--link" : ""}`}>
                        {piece.text}
                      </mark>
                    ) : (
                      <span key={i}>{piece.text}</span>
                    ),
                  )}
                  {/* A trailing newline needs a character after it to take up a line. */}
                  {"​"}
                </div>
                <textarea
                  id="em-body"
                  ref={bodyRef}
                  className="ta em-editor__input"
                  aria-describedby="em-body-hint"
                  value={body}
                  rows={16}
                  spellCheck
                  onFocus={() => (lastFocus.current = "body")}
                  onScroll={(e) => {
                    if (highlightRef.current) highlightRef.current.scrollTop = e.currentTarget.scrollTop;
                  }}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>
            </div>

            <div className="em-fields" aria-label="Details filled in for each client">
              <span className="lt-label">Details filled in for each client</span>
              <span className="lt-hint">Choose one to add it where your cursor is.</span>
              <ul className="em-fields__list">
                {fields.map((field) => (
                  <li key={field.name}>
                    <button type="button" className="em-chip em-chip--button" onClick={() => insert(field.label)}>
                      [{field.label}]
                    </button>
                    {field.required && <span className="em-fields__must">must stay in the email</span>}
                  </li>
                ))}
              </ul>
            </div>

            {missing.map((field) => (
              <p key={field.name} className="lt-error" role="alert">
                The email must keep [{field.label}]. Without it the client can&rsquo;t reach their form. Choose it above
                to put it back.
              </p>
            ))}

            {(failure !== null || problems.length > 0) && (
              <div className="lt-error" role="alert">
                {failure !== null && <p className="em-error__lead">{failure.message}</p>}
                {problems.length > 0 && (
                  <ul className="em-error__list">
                    {problems.map((p, i) => (
                      <li key={`${p.code}-${i}`}>{toEditor(p.message, fields)}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="em-actions">
              <Button variant="primary" disabled={!canSave} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              {dirty && (
                <Button variant="ghost" disabled={busy} onClick={discard}>
                  Discard changes
                </Button>
              )}
              <span className="em-actions__spacer" />
              {template.is_edited && !confirmReset && (
                <Button variant="ghost" disabled={busy} onClick={() => setConfirmReset(true)}>
                  Reset to default wording
                </Button>
              )}
            </div>

            {confirmReset && (
              <div className="em-confirm" role="alertdialog" aria-labelledby="em-confirm-h">
                <p id="em-confirm-h" className="em-confirm__title">
                  Put back the default wording?
                </p>
                <p className="lt-hint">Your firm&rsquo;s wording for this email will be removed. The next email sent will use the default.</p>
                <div className="em-actions">
                  <Button variant="destructive" disabled={busy} onClick={() => reset.mutate()}>
                    {reset.isPending ? "Resetting…" : "Reset to default"}
                  </Button>
                  <Button variant="ghost" disabled={busy} onClick={() => setConfirmReset(false)}>
                    Keep my wording
                  </Button>
                </div>
              </div>
            )}

            {template.is_edited && (
              <details className="em-default">
                <summary>See the default wording</summary>
                <p className="em-default__subject">{toEditor(template.default_subject, fields)}</p>
                <p className="em-default__body">{toEditor(template.default_body_text, fields)}</p>
              </details>
            )}
          </div>
        </section>

        <section className="im-card" aria-labelledby="em-preview-h">
          <div className="im-card__head">
            <h2 id="em-preview-h" className="im-card__title">
              What your client will see
            </h2>
            {preview.isFetching && <span className="lt-hint">Updating…</span>}
          </div>
          <div className="im-card__body">
            {preview.error !== null && (
              <p className="lt-hint" role="status">
                The preview could not be shown just now. Your wording is not affected.
              </p>
            )}
            {preview.data !== undefined && (
              <>
                <p className="em-preview__subject">
                  <span className="lt-hint">Subject</span>
                  <span data-testid="email-preview-subject">{preview.data.subject}</span>
                </p>
                <iframe
                  className="em-preview__frame"
                  title="Preview of the email"
                  sandbox=""
                  srcDoc={previewDocument(preview.data.body_html)}
                />
                <details className="em-default">
                  <summary>Plain-text version</summary>
                  <p className="em-default__body">{preview.data.body_text}</p>
                </details>
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

/** The preview frame's document: the email's HTML in a plain reading column. No colour is set here —
 * an email client draws it in its own. */
export function previewDocument(bodyHtml: string): string {
  return (
    "<!doctype html><html><head><meta charset=\"utf-8\"><style>" +
    "body{margin:16px;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;font-size:15px;line-height:1.5}" +
    "p{margin:0 0 12px}</style></head><body>" +
    bodyHtml +
    "</body></html>"
  );
}

export default EmailsPage;
